import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  useActivateBonusViaLink,
  useApiContext,
  useGetBonus,
  useGetBonusesTransactions
} from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { StepProps, StepsOption } from 'src/types';
import { useModalContext } from 'src/hooks/useModalContext';
import { useNavigate } from 'react-router';
import { SIGN_UP_BONUS_ID } from 'src/constants';
import { CONTENT_URL } from 'src/api';
import gift from 'src/assets/images/gift.webp';
import defaultImageBanner from 'src/assets/images/registration/defaultImageForRegistration.webp';
import defaultImageBackground from 'src/assets/images/registration/defaultImageForRegistrationMobile.webp';
import project from 'src/config/project.json';
import { CmsContent } from 'src/components/CmsContent';
import { ModalWrapper } from 'src/components/ModalWrapper';
import { StepOne } from './StepOne';
import { StepTwo } from './StepTwo';
import { StepThree } from './StepThree';
import { useRoute } from 'src/router/hooks';
import { twMerge } from 'tailwind-merge';
import { Skeletons } from 'src/components/Skeletons';
import { ACTIVATE_BONUS_LOCAL_STORAGE_KEY } from 'src/constants/activateBonusLocalStorageKey';

type ModalProps = {
  initialStep: number | undefined;
  hideWhyNot?: boolean;
  onAuthFlowSuccess?: () => void;
};

const SignUpModal = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useApiContext();
  const { modalProps, openModal } = useModalContext<ModalProps>();

  const [currentStep, setCurrentStep] = useState(modalProps?.initialStep || 0);
  const [availableSteps, setAvailableSteps] = useState<boolean[]>([
    false,
    modalProps?.initialStep === 1,
    false
  ]);
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([false, false, false]);
  const { mutateAsync: activateBonus } = useActivateBonusViaLink();
  const { refetch } = useGetBonusesTransactions(
    {
      bonus_type: 'All',
      status: ['Pending']
    },
    { isNeedPages: false }
  );

  const steps: StepsOption[] = useMemo(
    () => [
      {
        title: 'First',
        children: ({ changeStep, completedSteps }: StepProps) => (
          <StepOne
            isCompleted={completedSteps[0]}
            changeStep={changeStep}
            disableFields={completedSteps[0]}
          />
        )
      },
      {
        title: 'Second',
        children: ({ changeStep }: StepProps) => <StepTwo changeStep={changeStep} />
      },
      {
        title: 'Last',
        children: ({ completedSteps }: StepProps) => <StepThree disableFields={completedSteps[2]} />
      }
    ],
    []
  );

  const handleChangeStep = (newStep: number) => {
    setCurrentStep(newStep);

    const nextAvailableStepsState = [...availableSteps];
    const nextCompletedStepsState = [...completedSteps];

    // available
    nextAvailableStepsState[newStep] = true;

    // completed
    if (newStep !== 0) {
      nextCompletedStepsState[newStep - 1] = true;
    }

    setAvailableSteps(nextAvailableStepsState);
    setCompletedSteps(nextCompletedStepsState);
  };

  useEffect(() => {
    if (isAuthenticated && !completedSteps[0]) {
      const nextCompletedStepsState = [...completedSteps];
      nextCompletedStepsState[0] = true;
      setCompletedSteps(nextCompletedStepsState);
    }
    if (isAuthenticated && completedSteps[0]) {
      const bonusId = localStorage.getItem(ACTIVATE_BONUS_LOCAL_STORAGE_KEY);

      if (bonusId) {
        activateBonus(
          { bonus_id: bonusId },
          {
            onSuccess: async () => {
              refetch();
            }
          }
        );
        localStorage.removeItem(ACTIVATE_BONUS_LOCAL_STORAGE_KEY);
      }
    }
  }, [completedSteps, isAuthenticated]);

  const StepComponent = steps[currentStep].children;

  const handleClose = () => {
    if (!isAuthenticated && !modalProps?.hideWhyNot) {
      openModal('BACK_TO_SIGNUP', { replace: true });
    }
    if (isAuthenticated) {
      modalProps?.onAuthFlowSuccess?.();
    }
  };

  return (
    <ModalWrapper onClose={handleClose} className="max-w-full min-w-full 412:min-w-0">
      <div className="flex 1024:overflow-y-hidden flex-col min-h-[460px] w-full 412:w-[350px] 768:flex-row-reverse 768:w-[620px] 1024:w-[770px]">
        <SignupBonus />
        <div className="768:w-[50%] flex flex-1 768:flex-auto flex-col 768:min-h-[495px] 1024:min-h-[465px]">
          <div className="my-3 768:mb-4 flex justify-between 768:flex-col gap-1.5 768:gap-4 px-[15px]">
            <p className="font-bold 768:text-2xl text-xl uppercase 768:capitalize text-center text-sum_title overflow-hidden overflow-ellipsis">
              {t('general.signUp')}
            </p>
            <div className="flex justify-center items-center gap-[3px]">
              {steps.map((item, index) => {
                return (
                  <>
                    <div
                      key={item.title}
                      className={twMerge(
                        'flex text-[10px] text-sum_number_step font-semibold text-center justify-center items-center h-4 w-4 rounded-[4px] border-sum_border_steps border',
                        availableSteps[index]
                          ? 'cursor-pointer text-sum_border_steps'
                          : 'cursor-no-drop',
                        completedSteps[index] &&
                          'bg-sum_step_completed_bg text-sum_step_completed_text border-sum_step_completed_border',
                        currentStep === index &&
                          'bg-sum_step_bg border-sum_step_active_border text-sum_number_active_step'
                      )}
                      onClick={() => availableSteps[index] && handleChangeStep(index)}>
                      {index + 1}
                    </div>
                    <span
                      className={classNames(
                        'border-b border-dashed w-2',
                        completedSteps[index] || currentStep === index
                          ? 'border-sum_number_active_step'
                          : 'border-sum_border_steps'
                      )}
                    />
                  </>
                );
              })}
              <div className="flex justify-center items-center w-4 h-4 rounded-[4px] border-sum_border_steps border">
                <img src={gift} alt="gift" className="w-2" />
              </div>
            </div>
          </div>
          <div className="flex flex-1 px-[15px] pb-[30px]">
            <StepComponent
              changeStep={handleChangeStep}
              availableSteps={availableSteps}
              completedSteps={completedSteps}
            />
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

// eslint-disable-next-line no-restricted-exports
export default SignUpModal;

function SignupBonus() {
  const { data: bonus, isFetching, isFetched } = useGetBonus(SIGN_UP_BONUS_ID);
  const navigate = useNavigate();
  const { ROUTE } = useRoute();
  const { closeModal } = useModalContext();
  const { t } = useTranslation();
  return (
    <div
      className={classNames(
        'relative px-4 py-2 flex justify-end md:w-[50%] cursor-pointer',
        !bonus?.imageBackground && 'min-h-[70px]'
      )}
      onClick={() => {
        closeModal();
        navigate(ROUTE.BONUSES.get());
      }}>
      {!isFetched ? (
        <Skeletons count={1} className="w-full 768:h-[495px] h-[100px]" />
      ) : (
        <>
          {!isFetching && (
            <img
              src={`${
                bonus?.imageBackground
                  ? `${CONTENT_URL}${bonus?.imageBackground}`
                  : defaultImageBackground
              }`}
              alt="registration"
              className="rounded-t-[20px] w-full absolute h-full top-0 left-0 md:hidden"
            />
          )}
          {!isFetching && (
            <img
              src={`${
                bonus?.imageBanner && !isFetching
                  ? `${CONTENT_URL}${bonus?.imageBanner}`
                  : defaultImageBanner
              }`}
              alt="welcome bonus"
              className="w-full absolute hidden h-full top-0 landscape:object-center left-0 object-cover object-left-bottom md:block"
            />
          )}
          <div className="relative z-10 w-full md:pt-0 pt-[7%] md:w-[80%] text-left md:mx-auto md:mt-[50px]">
            <CmsContent
              className="font-bold text-[12px] text-left md:text-center text-sum_title_card md:text-[26px] md:mb-[2%] cms-dropdown"
              content={bonus?.name ? bonus?.name : ''}
            />
            {bonus?.shortDescription ? (
              <CmsContent
                className="font-bold text-base bonus-short-description-modal md:text-[48px] md:leading-[110%] cms-dropdown"
                content={bonus?.shortDescription}
              />
            ) : (
              !isFetching && (
                <p className="font-bold text-sum_link text-base bonus-short-description-modal md:text-[35px] md:leading-[110%] uppercase cms-dropdown pl-[40px]">
                  {t('registration.welcomeToFgFox', { projectName: project.projectNameUppercase })}
                </p>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}
